  (() => {
    window.__veronaScentQuizV9ScriptLoaded = true;

    const externalPopupSelectors = [
      '.needsclick[class*="kl-teaser"]',
      '.needsclick[class*="klaviyo"]',
      '.needsclick[role="dialog"]',
      '[class*="kl-teaser"]',
      '[class*="kl-private-reset-css"][role="dialog"]',
      'form[class*="klaviyo-form"]',
      '.klaviyo-form'
    ];

    const suppressExternalPopups = () => {
      document.documentElement.classList.add('verona-scent-quiz-clean-page');
      document.body?.classList.add('verona-scent-quiz-clean-page');
      externalPopupSelectors.forEach((selector) => {
        document.querySelectorAll(selector).forEach((element) => {
          if (element.closest('scent-quiz-v9')) return;
          element.setAttribute('aria-hidden', 'true');
          element.style.setProperty('display', 'none', 'important');
          element.style.setProperty('visibility', 'hidden', 'important');
          element.style.setProperty('opacity', '0', 'important');
          element.style.setProperty('pointer-events', 'none', 'important');
        });
      });
    };

    suppressExternalPopups();
    if ('MutationObserver' in window) {
      const externalPopupObserver = new MutationObserver(suppressExternalPopups);
      externalPopupObserver.observe(document.documentElement, { childList: true, subtree: true });
    }

    class ScentQuizV9 {
      constructor(element) {
        this.element = element;
      }

      get dataset() {
        return this.element.dataset;
      }

      querySelector(selector) {
        return this.element.querySelector(selector);
      }

      addEventListener(type, listener) {
        this.element.addEventListener(type, listener);
      }

      dispatchEvent(event) {
        this.element.dispatchEvent(event);
      }

      scrollIntoView(options) {
        this.element.scrollIntoView(options);
      }

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.data = this.readData();
        this.state = {
          screen: 'intro',
          index: 0,
          answers: {}
        };
        this.screens = {
          intro: this.querySelector('[data-quiz-screen="intro"]'),
          questions: this.querySelector('[data-quiz-screen="questions"]'),
          result: this.querySelector('[data-quiz-screen="result"]')
        };
        this.progressLabel = this.querySelector('[data-quiz-progress-label]');
        this.progressBar = this.querySelector('[data-quiz-progress-bar]');
        this.questionEyebrow = this.querySelector('[data-question-eyebrow]');
        this.questionTitle = this.querySelector('[data-question-title]');
        this.optionsContainer = this.querySelector('[data-question-options]');
        this.backButton = this.querySelector('[data-quiz-back]');
        this.nextButton = this.querySelector('[data-quiz-next]');

        this.querySelector('[data-quiz-start]')?.addEventListener('click', () => this.start());
        this.backButton?.addEventListener('click', () => this.back());
        this.nextButton?.addEventListener('click', () => this.next());
        this.optionsContainer?.addEventListener('click', (event) => {
          const option = event.target.closest('[data-option-index]');
          if (option) this.selectOption(Number(option.dataset.optionIndex));
        });
        this.addEventListener('click', (event) => this.handleClick(event));
        this.addEventListener('submit', (event) => {
          if (event.target.matches('[data-quiz-lead-form]')) this.captureLead(event);
        });
        this.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') this.closeDiscountModal();
        });
        this.queueAssetPreload();
      }

      readData() {
        const dataNode = this.querySelector('[data-scent-quiz-v9-data]');
        if (!dataNode) return { questions: [], scents: [], devices: [] };
        try {
          return JSON.parse(dataNode.textContent);
        } catch (error) {
          return { questions: [], scents: [], devices: [] };
        }
      }

      queueAssetPreload() {
        if (this.assetsPreloadQueued || this.assetsPreloadComplete) return;
        const run = () => {
          this.assetsPreloadComplete = true;
          [...(this.data.scents || []), ...(this.data.devices || [])]
            .map((item) => item.image)
            .filter(Boolean)
            .forEach((src) => {
              const image = new Image();
              image.decoding = 'async';
              image.src = src;
            });
        };
        const schedule = () => {
          if (this.assetsPreloadQueued || this.assetsPreloadComplete) return;
          this.assetsPreloadQueued = true;
          if ('requestIdleCallback' in window) {
            window.requestIdleCallback(run, { timeout: 1600 });
          } else {
            window.setTimeout(run, 400);
          }
        };
        if (!window.matchMedia) {
          schedule();
          return;
        }
        const mediaQuery = window.matchMedia('(min-width: 750px)');
        if (mediaQuery.matches) {
          schedule();
          return;
        }
        this.preloadMediaQuery = mediaQuery;
        this.preloadMediaQueryHandler = (event) => {
          if (!event.matches) return;
          this.removePreloadMediaQueryListener();
          schedule();
        };
        if (mediaQuery.addEventListener) {
          mediaQuery.addEventListener('change', this.preloadMediaQueryHandler);
        } else if (mediaQuery.addListener) {
          mediaQuery.addListener(this.preloadMediaQueryHandler);
        }
      }

      disconnectedCallback() {
        this.removePreloadMediaQueryListener();
      }

      removePreloadMediaQueryListener() {
        if (!this.preloadMediaQuery || !this.preloadMediaQueryHandler) return;
        if (this.preloadMediaQuery.removeEventListener) {
          this.preloadMediaQuery.removeEventListener('change', this.preloadMediaQueryHandler);
        } else if (this.preloadMediaQuery.removeListener) {
          this.preloadMediaQuery.removeListener(this.preloadMediaQueryHandler);
        }
        this.preloadMediaQuery = null;
        this.preloadMediaQueryHandler = null;
      }

      async handleClick(event) {
        if (event.target.closest('[data-quiz-restart]')) this.restart();
        if (event.target.closest('[data-unlock-discount]')) {
          event.preventDefault();
          this.openDiscountModal();
        }
        if (event.target.closest('[data-modal-close]')) {
          event.preventDefault();
          this.closeDiscountModal();
        }
        const continueButton = event.target.closest('[data-continue-plan]');
        if (continueButton) {
          event.preventDefault();
          await this.redirectToPlan(false, continueButton.dataset.planUrl);
        }
        const fallbackButton = event.target.closest('[data-continue-without-code]');
        if (fallbackButton) {
          event.preventDefault();
          await this.redirectToPlan(false, fallbackButton.dataset.planUrl);
        }
        const successButton = event.target.closest('[data-success-continue]');
        if (successButton) {
          event.preventDefault();
          await this.redirectToPlan(true, successButton.dataset.discountUrl || successButton.dataset.planUrl, successButton);
        }
        if (event.target.closest('[data-copy-discount]')) {
          event.preventDefault();
          this.copyDiscountCode(event.target.closest('[data-copy-discount]'));
        }
        if (event.target.closest('[data-success-back]')) {
          event.preventDefault();
          this.closeDiscountModal();
        }
      }

      showScreen(screen) {
        Object.entries(this.screens).forEach(([name, element]) => {
          if (!element) return;
          element.hidden = name !== screen;
        });
        this.state.screen = screen;
      }

      start() {
        this.state.index = 0;
        this.showScreen('questions');
        this.renderQuestion();
        this.scrollToQuestionStart();
      }

      restart() {
        this.state = {
          screen: 'intro',
          index: 0,
          answers: {}
        };
        this.showScreen('intro');
        this.scrollIntoView({ behavior: this.getScrollBehavior(), block: 'start' });
      }

      getActiveQuestions() {
        const selectedSpace = this.getAnswerLabel('space').toLowerCase();
        return (this.data.questions || []).filter((question) => {
          if (!Array.isArray(question.conditionSpaces) || !question.conditionSpaces.length) return true;
          return question.conditionSpaces.includes(selectedSpace);
        });
      }

      currentQuestion() {
        return this.getActiveQuestions()[this.state.index];
      }

      renderQuestion() {
        const activeQuestions = this.getActiveQuestions();
        if (this.state.index >= activeQuestions.length) this.state.index = activeQuestions.length - 1;
        const question = activeQuestions[this.state.index];
        if (!question) return;
        const answerIndex = this.state.answers[question.id];

        if (this.progressLabel) this.progressLabel.textContent = `Question ${this.state.index + 1} of ${activeQuestions.length}`;
        if (this.progressBar) this.progressBar.style.width = `${((this.state.index + 1) / activeQuestions.length) * 100}%`;
        if (this.questionEyebrow) this.questionEyebrow.textContent = question.eyebrow || '';
        if (this.questionTitle) this.questionTitle.textContent = question.title || '';
        if (this.backButton) this.backButton.disabled = this.state.index === 0;
        if (this.nextButton) {
          this.nextButton.disabled = typeof answerIndex !== 'number';
          this.nextButton.textContent = this.state.index === activeQuestions.length - 1 ? 'See my match' : 'Continue';
        }

        if (!this.optionsContainer) return;
        this.optionsContainer.innerHTML = (question.options || []).map((option, index) => {
          const selected = index === answerIndex;
          return `
            <button type="button" class="scent-quiz-v9__option${selected ? ' is-selected' : ''}" data-option-index="${index}" aria-pressed="${selected ? 'true' : 'false'}">
              <strong>${this.escapeHtml(option.label)}</strong>
              <span>${this.escapeHtml(option.detail)}</span>
            </button>
          `;
        }).join('');
      }

      selectOption(optionIndex) {
        const question = this.currentQuestion();
        if (!question || !question.options[optionIndex]) return;
        this.state.answers[question.id] = optionIndex;
        if (question.id === 'space') {
          const selectedSpace = this.getAnswerLabel('space').toLowerCase();
          const scaleQuestion = (this.data.questions || []).find((item) => item.id === 'scale');
          if (scaleQuestion && !scaleQuestion.conditionSpaces.includes(selectedSpace)) {
            delete this.state.answers.scale;
          }
        }
        this.renderQuestion();
        if (this.nextButton) this.nextButton.focus({ preventScroll: true });
      }

      back() {
        if (this.state.index === 0) return;
        this.state.index -= 1;
        this.renderQuestion();
        this.scrollToQuestionStart();
      }

      next() {
        const question = this.currentQuestion();
        const activeQuestions = this.getActiveQuestions();
        if (!question || typeof this.state.answers[question.id] !== 'number') return;
        if (this.state.index >= activeQuestions.length - 1) {
          this.renderResult();
          return;
        }
        this.state.index += 1;
        this.renderQuestion();
        this.scrollToQuestionStart();
      }

      getAnswerWeights() {
        return this.getActiveQuestions().reduce((weights, question) => {
          const answerIndex = this.state.answers[question.id];
          const option = question.options[answerIndex];
          if (!option || !option.weights) return weights;
          Object.entries(option.weights).forEach(([key, value]) => {
            weights[key] = (weights[key] || 0) + Number(value || 0);
          });
          return weights;
        }, {});
      }

      scoreScents(weights, context) {
        return (this.data.scents || []).map((scent) => {
          const profileScore = Object.entries(weights).reduce((total, [key, value]) => {
            return total + Number(value || 0) * Number((scent.profile && scent.profile[key]) || 0);
          }, 0);
          const spaceBoost = Number((scent.spaceBoosts && scent.spaceBoosts[context.space]) || 0);
          const fitBoost = this.getSpaceFitBonus(scent, context);
          const preferenceBoost = this.getPreferenceBonus(scent, context);
          const score = profileScore + spaceBoost + fitBoost + preferenceBoost;
          return { scent, score };
        }).sort((a, b) => b.score - a.score);
      }

      getSpaceFitBonus(scent, context) {
        if (!context || !context.space) return 0;
        let bonus = 0;
        const fit = Array.isArray(scent.spaceFit) ? scent.spaceFit : [];
        const bestFor = String(scent.bestFor || '').toLowerCase();
        if (fit.includes(context.space)) bonus += 1.5;
        if (context.space === 'store / showroom' && /hosting|entryway|living|whole/.test(bestFor)) bonus += 0.75;
        if (context.space === 'whole home' && /whole|signature|ambience/.test(bestFor)) bonus += 0.75;
        if (context.space === 'office' && /office|focus|daily/.test(bestFor)) bonus += 0.75;
        if (context.space === 'bedroom' && /bedroom|quiet|evening|calm/.test(bestFor)) bonus += 0.75;
        if (context.space === 'living room' && /living|hosting|open/.test(bestFor)) bonus += 0.75;
        if (context.space === 'car' && /car|compact|drive/.test(bestFor)) bonus += 0.75;
        return bonus;
      }

      getPreferenceBonus(scent, context) {
        const handle = scent.handle;
        let bonus = 0;
        const moodMap = {
          'clean and airy': { hope: 6, 'shangri-la': 3, amar: 2, 'sea-front': 1 },
          'luxury hotel lobby': { 'my-way': 5, 'shangri-la': 4, santale: 3, 'square-gather': 2 },
          'calm spa': { 'spa-now': 5, amar: 4, hope: 2, 'shangri-la': 1 },
          'bright and energizing': { lemongrass: 6, hope: 5, 'exotic-tea': 4, 'square-gather': 2 },
          'warm and sensual': { santale: 5, 'my-way': 3, amar: 2 },
          'coastal and fresh': { 'sea-front': 6, hope: 2, 'shangri-la': 1 }
        };
        const noteMap = {
          citrus: { lemongrass: 8, 'square-gather': 4, hope: 2, 'exotic-tea': 3 },
          'white florals': { 'my-way': 6, 'shangri-la': 4, 'square-gather': 2 },
          'green tea & bamboo': { amar: 6, 'shangri-la': 4, 'exotic-tea': 5, hope: 2 },
          'sea salt': { 'sea-front': 7 },
          herbs: { 'spa-now': 6, hope: 5, amar: 2 },
          'sandalwood & musk': { santale: 7, amar: 3, 'my-way': 2 }
        };
        const momentMap = {
          'morning reset': { lemongrass: 4, hope: 5, 'sea-front': 2 },
          'work focus': { hope: 5, amar: 4, 'exotic-tea': 3, 'shangri-la': 2 },
          'evening unwind': { santale: 4, 'spa-now': 3, amar: 2 },
          'hosting guests': { 'my-way': 4, 'square-gather': 4, santale: 3, 'shangri-la': 2 },
          'everyday signature': { 'shangri-la': 4, 'my-way': 3, santale: 3, amar: 2 }
        };
        [moodMap[context.mood], noteMap[context.notes], momentMap[context.moment]].forEach((map) => {
          if (map && map[handle]) bonus += map[handle];
        });
        if (context.avoid === 'skip heavy florals' && handle === 'my-way') bonus -= 5;
        if (context.avoid === 'skip herbal spa' && handle === 'spa-now') bonus -= 5;
        if (context.avoid === 'skip dense woods' && handle === 'santale') bonus -= 5;
        if (context.avoid === 'skip aquatic notes' && handle === 'sea-front') bonus -= 5;
        if (context.avoid === 'skip sweet notes' && (handle === 'my-way' || handle === 'santale')) bonus -= 2;
        return bonus;
      }

      renderResult() {
        const weights = this.getAnswerWeights();
        const context = this.getQuizContext();
        const ranked = this.scoreScents(weights, context);
        const top = ranked[0];
        if (!top || !this.screens.result) return;

        const secondScore = ranked[1] ? ranked[1].score : top.score;
        const match = this.calculateMatch(top.score, secondScore);
        const scent = top.scent;
        const recommendation = this.getDeviceRecommendation(context);
        const subscriptionUrl = this.buildSubscriptionUrl(scent.handle, recommendation);
        const reasons = this.buildReasons(scent, weights, context);
        const notePills = this.getNotePills(scent.notes);
        const upsells = this.buildUpsells(ranked, context);

        this.currentPlanUrl = subscriptionUrl;
        this.currentDiscountUrl = '';
        this.currentDiscountCode = '';
        this.currentResult = this.buildResultPayload(scent, match.value, recommendation, context, subscriptionUrl);

        this.screens.result.innerHTML = `
          <div class="scent-quiz-v9__result-shell">
            <div class="scent-quiz-v9__result-media">
              <a href="${this.escapeAttribute(scent.url)}" aria-label="View ${this.escapeAttribute(scent.title)}">
                <img src="${this.escapeAttribute(scent.image)}" alt="${this.escapeAttribute(scent.title)} scent oil" width="900" height="900" loading="eager" decoding="async">
              </a>
              <div class="scent-quiz-v9__match"><strong>${match.value}%</strong><span>${this.escapeHtml(match.label)}</span></div>
            </div>
            <div class="scent-quiz-v9__result-copy">
              <p class="scent-quiz-v9__result-kicker">Your scent match</p>
              <h2><a href="${this.escapeAttribute(scent.url)}">${this.escapeHtml(scent.title)}</a></h2>
              <p class="scent-quiz-v9__result-summary">${this.escapeHtml(scent.summary)}</p>
              <div class="scent-quiz-v9__note-pills">
                ${notePills.map((note) => `<span>${this.escapeHtml(note)}</span>`).join('')}
              </div>
              <div class="scent-quiz-v9__why">
                ${reasons.map((reason) => `<span>${this.escapeHtml(reason)}</span>`).join('')}
              </div>
              <div class="scent-quiz-v9__offer-panel">
                <div>
                  <p>Recommended next step</p>
                  <h3>Start your Verona monthly plan</h3>
                  <span>The easiest way to begin: review your recommended ${this.escapeHtml(recommendation.size)} scent plan and include your matched ${this.escapeHtml(recommendation.title)} complimentary. Nothing is final until checkout.</span>
                </div>
                <button type="button" class="button scent-quiz-v9__primary-offer" data-unlock-discount data-plan-url="${this.escapeAttribute(subscriptionUrl)}">
                  <span>Unlock 10% for my first order</span>
                  <small>Get a private code, then review your matched plan</small>
                </button>
                <button type="button" class="scent-quiz-v9__plan-link" data-continue-plan data-plan-url="${this.escapeAttribute(subscriptionUrl)}">Go to subscription offer</button>
              </div>
            </div>

            <div class="scent-quiz-v9__pairing-card">
              <a class="scent-quiz-v9__pairing-device" href="${this.escapeAttribute(recommendation.url || '#')}" aria-label="View ${this.escapeAttribute(recommendation.title)}">
                <img src="${this.escapeAttribute(recommendation.image)}" alt="${this.escapeAttribute(recommendation.title)} diffuser" width="420" height="420" loading="lazy" decoding="async">
              </a>
              <div class="scent-quiz-v9__pairing-copy">
                <p>Your diffuser match</p>
                <h3><a href="${this.escapeAttribute(recommendation.url || '#')}">${this.escapeHtml(recommendation.title)}</a></h3>
                <div class="scent-quiz-v9__pairing-facts">
                  <span><b>${this.escapeHtml(recommendation.size)}</b> monthly plan</span>
                  <span><b>${this.escapeHtml(this.toTitleCase(context.space))}</b> setup</span>
                  <span>${this.escapeHtml(recommendation.bestFor)}</span>
                </div>
                <span class="scent-quiz-v9__pairing-reason">${this.escapeHtml(recommendation.reason)}</span>
              </div>
            </div>

            <div class="scent-quiz-v9__actions">
              <button type="button" class="scent-quiz-v9__text-button" data-quiz-restart>Retake quiz</button>
            </div>

            <div class="scent-quiz-v9__alternatives">
              <h3>Build your scent wardrobe</h3>
              <div class="scent-quiz-v9__alternative-list">
                ${upsells.map((item) => `
                  <a class="scent-quiz-v9__alternative" href="${this.escapeAttribute(item.scent.url)}">
                    <img src="${this.escapeAttribute(item.scent.image)}" alt="${this.escapeAttribute(item.scent.title)} scent oil" width="144" height="144" loading="lazy" decoding="async">
                    <span>
                      <em>${this.escapeHtml(item.label)}</em>
                      <strong>${this.escapeHtml(item.scent.title)}</strong>
                      <span>${this.escapeHtml(item.reason)}</span>
                    </span>
                  </a>
                `).join('')}
              </div>
            </div>
          </div>
          ${this.renderDiscountModal(scent, recommendation)}
        `;

        this.persistResult(this.currentResult);
        this.dispatchEvent(new CustomEvent('verona_scent_quiz_completed', {
          bubbles: true,
          detail: this.currentResult
        }));
        this.showScreen('result');
        this.screens.result.scrollIntoView({ behavior: this.getScrollBehavior(), block: 'start' });
      }

      calculateMatch(topScore, secondScore) {
        const gap = Math.max(0, topScore - secondScore);
        const value = Math.max(75, Math.min(96, Math.round(75 + Math.min(gap * 0.9, 14) + Math.min(topScore / 55, 7))));
        return {
          value,
          label: gap <= 4 ? 'Close match' : 'Match'
        };
      }

      buildReasons(scent, weights, context) {
        const strongest = Object.entries(weights)
          .sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))
          .filter((entry) => entry[1] > 0)
          .slice(0, 2)
          .map(([key]) => key);
        const labels = {
          fresh: 'Clean, polished freshness for everyday scenting.',
          citrus: 'A bright lift with a refined Verona finish.',
          green: 'Leafy, modern notes that keep the room clear.',
          floral: 'Elegant floral depth without feeling too heavy.',
          woody: 'A smooth base that gives the scent lasting presence.',
          aquatic: 'A breezy, coastal impression with quiet freshness.',
          herbal: 'Botanical calm for a spa-like atmosphere.',
          warm: 'Soft warmth for evenings and intimate spaces.',
          spa: 'Restorative notes that feel balanced and serene.',
          luxury: 'A hotel-inspired signature that feels elevated.',
          energy: 'Enough brightness to support daytime momentum.',
          calm: 'Soft, grounded scenting that is easy to live with.',
          hosting: 'A memorable first impression for guests and customers.'
        };
        const reasons = [];
        reasons.push(`Made for ${this.toTitleCase(context.space).toLowerCase()} scenting with your preferred ${context.intensity || 'balanced'} presence.`);
        strongest.forEach((key) => {
          if (labels[key] && reasons.length < 3) reasons.push(labels[key]);
        });
        if (reasons.length < 3) reasons.push(`${scent.mood} fits the way you want the space to feel.`);
        return reasons.slice(0, 3);
      }

      buildUpsells(ranked, context) {
        const pool = ranked.slice(1).map((item) => item.scent);
        const labels = [
          { label: 'Next month', reason: 'A natural second refill for the same space.' },
          { label: 'More evening depth', reason: 'A richer layer when you want the room to feel warmer.' },
          { label: 'Fresh rotation', reason: 'A cleaner change of pace for daytime scenting.' }
        ];
        return pool.slice(0, 3).map((scent, index) => ({
          scent,
          label: labels[index]?.label || 'Also consider',
          reason: labels[index]?.reason || scent.mood
        }));
      }

      getDeviceRecommendation(context) {
        const devices = this.data.devices || [];
        const scale = context.scale || '';
        const space = context.space || '';
        const intensity = context.intensity || 'balanced';
        const moment = context.moment || '';
        let key = 'whisper';

        if (space === 'car') {
          key = 'benz';
        } else if (space === 'bedroom') {
          key = 'whisper';
        } else if (space === 'whole home') {
          key = scale === 'open area' && intensity === 'soft' ? 'aura' : 'grand';
        } else if (space === 'living room') {
          key = scale === 'private room' && intensity === 'soft' && moment !== 'hosting guests' ? 'whisper' : 'aura';
          if (scale === 'whole space / hvac') key = 'grand';
        } else if (space === 'office') {
          key = scale === 'open area' ? 'aura' : 'whisper';
          if (scale === 'whole space / hvac') key = 'grand';
        } else if (space === 'store / showroom') {
          key = scale === 'whole space / hvac' ? 'grand' : 'aura';
        }

        const device = devices.find((item) => item.key === key) || devices[0] || {
          key: 'whisper',
          title: 'Verona Whisper',
          url: '/products/verona-whisper',
          plan: '60ml monthly plan',
          size: '60ml',
          image: '',
          bestFor: 'Bedrooms, offices, and medium rooms'
        };
        return {
          ...device,
          reason: this.buildDeviceReason(device, context)
        };
      }

      buildDeviceReason(device, context) {
        const space = this.toTitleCase(context.space);
        const scale = context.scale ? this.toTitleCase(context.scale) : '';
        if (device.key === 'benz') {
          return `Benz keeps ${space.toLowerCase()} scenting close and polished, ideal for a luxury touch in compact daily spaces.`;
        }
        if (device.key === 'grand') {
          return `Grand is the strongest match for ${space.toLowerCase()} scenting when you need larger coverage, consistency, or HVAC-ready diffusion.`;
        }
        if (device.key === 'aura') {
          return `Aura gives ${space.toLowerCase()} the presence of a larger-room diffuser while keeping the setup refined and residential.`;
        }
        return `Whisper fits ${space.toLowerCase()} scenting when the goal is quiet control, comfort, and a balanced monthly refill.`;
      }

      renderDiscountModal(scent, recommendation) {
        return `
          <div class="scent-quiz-v9__modal" data-discount-modal hidden>
            <button type="button" class="scent-quiz-v9__modal-scrim" aria-label="Close offer form" data-modal-close></button>
            <div class="scent-quiz-v9__modal-dialog" role="dialog" aria-modal="true" aria-labelledby="scent-quiz-v9-modal-title">
              <button type="button" class="scent-quiz-v9__modal-close" aria-label="Close" data-modal-close>&times;</button>
              <form class="scent-quiz-v9__modal-lead" data-quiz-lead-form novalidate>
                <p>Save your match</p>
                <h3 id="scent-quiz-v9-modal-title">Save your Verona match and unlock 10% off your first order.</h3>
                <span>We'll save your scent, diffuser, and refill recommendation so your plan is ready to review.</span>
                <div class="scent-quiz-v9__lead-fields">
                  <label>
                    <span>First name</span>
                    <input type="text" name="first_name" autocomplete="given-name" required>
                  </label>
                  <label>
                    <span>Email</span>
                    <input type="email" name="email" autocomplete="email" required>
                  </label>
                  <label>
                    <span>Phone optional</span>
                    <input type="tel" name="phone" autocomplete="tel">
                  </label>
                </div>
                <label class="scent-quiz-v9__consent">
                  <input type="checkbox" name="consent" required>
                  <span>Email me my Verona match, recommendation, and offer details.</span>
                </label>
                <p class="scent-quiz-v9__lead-error" data-quiz-lead-error hidden>Please enter your first name, a valid email, and confirm the checkbox. Phone is optional.</p>
                <button type="button" class="scent-quiz-v9__plan-link" data-continue-without-code hidden>Continue to my matched plan without code</button>
                <button type="submit" class="button" data-lead-submit>Save my match</button>
              </form>
              <div class="scent-quiz-v9__modal-success" data-lead-success hidden>
                <p>Private offer</p>
                <h3>Your private offer is ready.</h3>
                <span>We saved your scent, diffuser, and refill recommendation so your matched plan is ready to review.</span>
                <div class="scent-quiz-v9__code-card" data-code-card hidden>
                  <span>Your first-order code</span>
                  <strong data-discount-code-label></strong>
                </div>
                <div class="scent-quiz-v9__modal-actions">
                  <button type="button" class="button" data-success-continue>Apply &amp; continue to my matched plan</button>
                  <button type="button" class="scent-quiz-v9__copy-button" data-copy-discount>Copy code</button>
                  <button type="button" class="scent-quiz-v9__plan-link" data-success-back>Back to my result</button>
                </div>
              </div>
            </div>
          </div>
        `;
      }

      openDiscountModal() {
        const modal = this.querySelector('[data-discount-modal]');
        if (!modal) return;
        modal.hidden = false;
        this.dispatchEvent(new CustomEvent('verona_scent_quiz_subscription_intent', {
          bubbles: true,
          detail: {
            ...(this.currentResult || {}),
            path: 'discount'
          }
        }));
        window.setTimeout(() => {
          modal.querySelector('input[name="first_name"]')?.focus({ preventScroll: true });
        }, 30);
      }

      closeDiscountModal() {
        const modal = this.querySelector('[data-discount-modal]');
        if (!modal || modal.hidden) return;
        modal.hidden = true;
      }

      async redirectToPlan(withDiscount, explicitTarget, triggerButton) {
        const planUrl = this.resolvePlanUrl();
        const target = withDiscount
          ? (explicitTarget || this.currentDiscountUrl || this.buildDiscountedUrl(planUrl, this.currentDiscountCode))
          : (explicitTarget || planUrl);
        this.dispatchEvent(new CustomEvent('verona_scent_quiz_subscription_intent', {
          bubbles: true,
          detail: {
            ...(this.currentResult || {}),
            path: withDiscount ? 'discount' : 'direct'
          }
        }));
        if (withDiscount) {
          this.setRedirectLoading(triggerButton, true);
          await this.clearCartDiscounts();
        }
        if (target) window.location.href = target;
      }

      async clearCartDiscounts() {
        try {
          await fetch('/cart/update.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ discount: '' })
          });
        } catch (clearError) {
          // Best effort: new v9 codes are non-combinable, and the discount redirect still applies the newest private code.
        }
      }

      setRedirectLoading(button, isLoading) {
        if (!button) return;
        if (isLoading) {
          button.dataset.originalText = button.textContent;
          button.textContent = 'Applying offer...';
          button.disabled = true;
          button.setAttribute('aria-busy', 'true');
        } else {
          button.textContent = button.dataset.originalText || 'Apply & continue to my matched plan';
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      }

      scrollToQuestionStart() {
        const target = this.querySelector('[data-quiz-screen="questions"]');
        if (!target) return;
        window.requestAnimationFrame(() => {
          target.scrollIntoView({
            behavior: this.getScrollBehavior(),
            block: 'start'
          });
        });
      }

      async captureLead(event) {
        event.preventDefault();
        const form = event.target;
        const firstName = String(form.elements.first_name?.value || '').trim();
        const email = String(form.elements.email?.value || '').trim();
        const phone = String(form.elements.phone?.value || '').trim();
        const consent = Boolean(form.elements.consent?.checked);
        const error = form.querySelector('[data-quiz-lead-error]');
        const submit = form.querySelector('[data-lead-submit]');
        this.hideLeadFallback(form);
        if (!firstName || !this.isValidEmail(email) || !consent) {
          this.showLeadError(error, 'Please enter your first name, a valid email, and confirm the checkbox. Phone is optional.');
          return;
        }
        if (error) error.hidden = true;
        const leadPayload = {
          first_name: firstName,
          email,
          phone,
          consent,
          consent_channel: 'email',
          consent_source: 'Verona Scent Quiz',
          klaviyo_list_id: this.dataset.klaviyoListId || '',
          quiz_result: this.currentResult || null,
          saved_at: new Date().toISOString()
        };
        try {
          this.setLeadLoading(submit, true);
          const offer = await this.createPrivateOffer(leadPayload);
          leadPayload.discount_code = offer.discount_code;
          this.currentPlanUrl = offer.subscription_url || this.resolvePlanUrl();
          const clientDiscountUrl = this.buildDiscountedUrl(this.currentPlanUrl, offer.discount_code);
          leadPayload.discount_url = clientDiscountUrl || offer.discount_url;
          leadPayload.discount_expires_at = offer.expires_at;
          leadPayload.klaviyo_synced = offer.klaviyo_synced;
          this.currentDiscountCode = offer.discount_code || '';
          this.currentDiscountUrl = clientDiscountUrl || offer.discount_url || this.buildDiscountedUrl(this.currentPlanUrl, this.currentDiscountCode);
          try {
            window.localStorage.setItem('verona_scent_quiz_v9_lead', JSON.stringify(leadPayload));
          } catch (storageError) {
            // Storage can be unavailable in private browsing; Klaviyo remains the source of truth.
          }
          this.sendKlaviyoLead(leadPayload);
          this.dispatchEvent(new CustomEvent('verona_scent_quiz_v9_lead_captured', {
            bubbles: true,
            detail: leadPayload
          }));
          this.dispatchEvent(new CustomEvent('verona_scent_quiz_lead_captured', {
            bubbles: true,
            detail: leadPayload
          }));
          this.showLeadSuccess(form, offer);
        } catch (requestError) {
          this.showLeadError(error, 'We could not create your private offer just now. Please try again, or continue to the subscription offer without the first-order code.');
          this.showLeadFallback(form);
        } finally {
          this.setLeadLoading(submit, false);
        }
      }

      async createPrivateOffer(payload) {
        const endpoint = String(this.dataset.offerEndpointUrl || '/apps/verona-quiz-offer/quiz-offer').trim() || '/apps/verona-quiz-offer/quiz-offer';
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        const timeoutId = controller ? window.setTimeout(() => controller.abort(), 12000) : null;
        try {
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload),
            signal: controller?.signal
          });
          const body = await response.json().catch(() => ({}));
          if (!response.ok || !body.discount_code || !body.discount_url) {
            throw new Error(body.error || `Offer request failed with ${response.status}.`);
          }
          return body;
        } finally {
          if (timeoutId) window.clearTimeout(timeoutId);
        }
      }

      sendKlaviyoLead(payload) {
        try {
          if (!window._learnq || !Array.isArray(window._learnq)) return;
          const identifyPayload = {
            '$email': payload.email,
            '$first_name': payload.first_name,
            verona_quiz_version: 'v9',
            verona_quiz_discount_code: payload.discount_code,
            verona_quiz_discount_url: payload.discount_url,
            verona_quiz_discount_expires_at: payload.discount_expires_at,
            verona_quiz_scent_handle: payload.quiz_result?.scent_handle,
            verona_quiz_scent_title: payload.quiz_result?.scent_title,
            verona_quiz_device_key: payload.quiz_result?.device_key,
            verona_quiz_device_title: payload.quiz_result?.device_title,
            verona_quiz_plan_size: payload.quiz_result?.plan_size,
            verona_quiz_match_score: payload.quiz_result?.match_score
          };
          if (payload.phone && /^\+[1-9]\d{7,14}$/.test(payload.phone)) {
            identifyPayload.$phone_number = payload.phone;
          } else if (payload.phone) {
            identifyPayload.verona_quiz_phone_optional = payload.phone;
          }
          window._learnq.push(['identify', identifyPayload]);
          window._learnq.push(['track', 'Verona Scent Quiz v9 Lead Captured', {
            scent_handle: payload.quiz_result?.scent_handle,
            scent_title: payload.quiz_result?.scent_title,
            device_key: payload.quiz_result?.device_key,
            device_title: payload.quiz_result?.device_title,
            plan_size: payload.quiz_result?.plan_size,
            discount_code: payload.discount_code,
            discount_url: payload.discount_url,
            quiz_context: payload.quiz_result?.context,
            answers: payload.quiz_result?.answers
          }]);
        } catch (error) {
          // Klaviyo is optional in v9; the redirect should never depend on it.
        }
      }

      async submitKlaviyoSubscription(payload) {
        const publicKey = String(this.dataset.klaviyoPublicKey || '').trim();
        const listId = String(this.dataset.klaviyoListId || '').trim();
        const revision = String(this.dataset.klaviyoRevision || '2026-04-15').trim();
        if (!publicKey || !listId) {
          throw new Error('Missing Klaviyo public key or list ID.');
        }
        const result = payload.quiz_result || {};
        const properties = {
          verona_quiz_version: 'v9',
          verona_quiz_context: result.context || this.dataset.context || 'standalone',
          verona_quiz_scent_handle: result.scent_handle || '',
          verona_quiz_scent_title: result.scent_title || '',
          verona_quiz_device_key: result.device_key || '',
          verona_quiz_device_title: result.device_title || '',
          verona_quiz_plan_size: result.plan_size || '',
          verona_quiz_match_score: result.match_score || '',
          verona_quiz_subscription_url: result.subscription_url || this.currentPlanUrl || '',
          verona_quiz_discount_path: this.currentDiscountUrl || '',
          verona_quiz_discount_code: payload.discount_code || '',
          verona_quiz_discount_url: payload.discount_url || '',
          verona_quiz_discount_expires_at: payload.discount_expires_at || '',
          verona_quiz_answers: result.answers || {},
          verona_quiz_saved_at: payload.saved_at
        };
        if (payload.phone) properties.verona_quiz_phone_optional = payload.phone;
        const profileAttributes = {
          email: payload.email,
          first_name: payload.first_name,
          properties
        };
        if (payload.phone && /^\+[1-9]\d{7,14}$/.test(payload.phone)) {
          profileAttributes.phone_number = payload.phone;
        }

        const response = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(publicKey)}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/vnd.api+json',
            revision
          },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'Verona Scent Quiz',
                profile: {
                  data: {
                    type: 'profile',
                    attributes: profileAttributes
                  }
                }
              },
              relationships: {
                list: {
                  data: {
                    type: 'list',
                    id: listId
                  }
                }
              }
            }
          })
        });
        if (!response.ok) {
          throw new Error(`Klaviyo subscription failed with ${response.status}.`);
        }
      }

      showLeadError(errorElement, message) {
        if (!errorElement) return;
        errorElement.textContent = message;
        errorElement.hidden = false;
      }

      showLeadFallback(form) {
        const fallback = form?.querySelector('[data-continue-without-code]');
        if (!fallback) return;
        fallback.dataset.planUrl = this.resolvePlanUrl();
        fallback.hidden = false;
      }

      hideLeadFallback(form) {
        const fallback = form?.querySelector('[data-continue-without-code]');
        if (!fallback) return;
        fallback.hidden = true;
      }

      setLeadLoading(button, isLoading) {
        if (!button) return;
        button.disabled = isLoading;
        button.textContent = isLoading ? 'Creating your private code...' : 'Save my match';
      }

      showLeadSuccess(form, offer) {
        const modal = this.querySelector('[data-discount-modal]');
        const success = modal?.querySelector('[data-lead-success]');
        if (form) form.hidden = true;
        this.hideLeadFallback(form);
        if (success) {
          const codeCard = success.querySelector('[data-code-card]');
          const codeLabel = success.querySelector('[data-discount-code-label]');
          const continueButton = success.querySelector('[data-success-continue]');
          if (codeLabel) codeLabel.textContent = offer?.discount_code || this.currentDiscountCode || '';
          if (codeCard) codeCard.hidden = !(offer?.discount_code || this.currentDiscountCode);
          if (continueButton) {
            continueButton.dataset.planUrl = this.resolvePlanUrl();
            continueButton.dataset.discountUrl = this.currentDiscountUrl || this.buildDiscountedUrl(this.resolvePlanUrl(), this.currentDiscountCode);
          }
          success.hidden = false;
          continueButton?.focus({ preventScroll: true });
        }
      }

      async copyDiscountCode(button) {
        const code = this.currentDiscountCode || this.querySelector('[data-discount-code-label]')?.textContent?.trim();
        if (!code) return;
        const original = button?.textContent || 'Copy code';
        try {
          if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(code);
          } else {
            const input = document.createElement('input');
            input.value = code;
            input.setAttribute('readonly', '');
            input.style.position = 'fixed';
            input.style.opacity = '0';
            document.body.appendChild(input);
            input.select();
            document.execCommand('copy');
            input.remove();
          }
          if (button) {
            button.textContent = 'Copied';
            window.setTimeout(() => {
              button.textContent = original;
            }, 1600);
          }
        } catch (error) {
          if (button) button.textContent = code;
        }
      }

      getAnswerLabel(questionId) {
        const question = (this.data.questions || []).find((item) => item.id === questionId);
        if (!question) return '';
        const answerIndex = this.state.answers[question.id];
        const option = question.options[answerIndex];
        return option ? option.label : '';
      }

      getQuizContext() {
        return {
          space: this.getAnswerLabel('space').toLowerCase(),
          scale: this.getAnswerLabel('scale').toLowerCase(),
          mood: this.getAnswerLabel('mood').toLowerCase(),
          notes: this.getAnswerLabel('notes').toLowerCase(),
          intensity: this.getAnswerLabel('intensity').toLowerCase(),
          moment: this.getAnswerLabel('moment').toLowerCase(),
          avoid: this.getAnswerLabel('avoid').toLowerCase()
        };
      }

      buildSubscriptionUrl(handle, recommendation) {
        const base = this.dataset.subscriptionUrl || '/pages/subscription-offer';
        try {
          const url = new URL(base, window.location.origin);
          url.searchParams.set('quiz_scent', handle);
          if (recommendation) {
            url.searchParams.set('quiz_device', recommendation.key);
            url.searchParams.set('quiz_plan', recommendation.size);
          }
          return url.pathname + url.search + url.hash;
        } catch (error) {
          const separator = base.includes('?') ? '&' : '?';
          const deviceParams = recommendation ? `&quiz_device=${encodeURIComponent(recommendation.key)}&quiz_plan=${encodeURIComponent(recommendation.size)}` : '';
          return `${base}${separator}quiz_scent=${encodeURIComponent(handle)}${deviceParams}`;
        }
      }

      buildDiscountedUrl(targetUrl, discountCode) {
        const code = String(discountCode || '').trim();
        if (!code) return targetUrl;
        if (this.isThemePreviewMode()) return targetUrl;
        try {
          const redirect = new URL(targetUrl, window.location.origin);
          const discountUrl = new URL(`/discount/${encodeURIComponent(code)}`, window.location.origin);
          discountUrl.searchParams.set('redirect', redirect.pathname + redirect.search + redirect.hash);
          return discountUrl.pathname + discountUrl.search;
        } catch (error) {
          return `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(targetUrl)}`;
        }
      }

      isThemePreviewMode() {
        return window.Shopify?.theme?.role === 'unpublished';
      }

      resolvePlanUrl() {
        if (this.currentPlanUrl) return this.currentPlanUrl;
        const result = this.currentResult || {};
        if (result.subscription_url) return result.subscription_url;
        if (result.scent_handle) {
          return this.buildSubscriptionUrl(result.scent_handle, {
            key: result.device_key || '',
            size: result.plan_size || ''
          });
        }
        return this.dataset.subscriptionUrl || '/pages/subscription-offer';
      }

      isValidEmail(email) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      }

      buildResultPayload(scent, match, recommendation, context, subscriptionUrl) {
        return {
          scent_handle: scent.handle,
          scent_title: scent.title,
          scent_url: scent.url,
          match_score: match,
          device_key: recommendation.key,
          device_title: recommendation.title,
          device_url: recommendation.url || '',
          plan_size: recommendation.size,
          plan_label: recommendation.plan,
          subscription_url: subscriptionUrl || '',
          answers: context,
          context: this.dataset.context || 'standalone',
          saved_at: new Date().toISOString()
        };
      }

      getNotePills(notes) {
        return String(notes || '')
          .split(',')
          .map((note) => note.trim())
          .filter(Boolean)
          .slice(0, 5);
      }

      toTitleCase(value) {
        return String(value || '').replace(/\w\S*/g, (word) => {
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        });
      }

      persistResult(payload) {
        try {
          window.localStorage.setItem('verona_scent_quiz_v9_result', JSON.stringify(payload));
        } catch (error) {
          // Storage can be unavailable in private browsing; the quiz still works without it.
        }
      }

      prefersReducedMotion() {
        return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      getScrollBehavior() {
        if (window.matchMedia && window.matchMedia('(max-width: 749px)').matches) return 'auto';
        return this.prefersReducedMotion() ? 'auto' : 'smooth';
      }

      escapeHtml(value) {
        return String(value || '').replace(/[&<>"']/g, (character) => ({
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;'
        }[character]));
      }

      escapeAttribute(value) {
        return this.escapeHtml(value).replace(/`/g, '&#96;');
      }
    }

    const initializeQuiz = (element) => {
      if (!element || element.__veronaScentQuizV9) return;
      const instance = new ScentQuizV9(element);
      element.__veronaScentQuizV9 = instance;
      instance.connectedCallback();
    };

    const initializeAllQuizzes = () => {
      document.querySelectorAll('scent-quiz-v9').forEach(initializeQuiz);
    };

    if (window.customElements && !window.customElements.get('scent-quiz-v9')) {
      window.customElements.define('scent-quiz-v9', class ScentQuizV9Element extends HTMLElement {
        connectedCallback() {
          initializeQuiz(this);
        }

        disconnectedCallback() {
          this.__veronaScentQuizV9?.disconnectedCallback?.();
        }
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initializeAllQuizzes, { once: true });
    } else {
      initializeAllQuizzes();
    }
    window.addEventListener('load', initializeAllQuizzes, { once: true });
    window.setTimeout(initializeAllQuizzes, 0);
    window.setTimeout(initializeAllQuizzes, 250);
    window.setTimeout(initializeAllQuizzes, 1000);
  })();
